import { useLocation, Link } from 'react-router-dom';
import { useAuth, useBusinessUnit } from '@/App';
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LayoutDashboard,
  DollarSign,
  Users,
  FileText,
  Receipt,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Building2,
  Settings,
  UserCircle,
  Home,
  Briefcase,
  Upload,
  PieChart,
  UserPlus,
  BarChart3,
  ScrollText,
  Bell,
  Mail,
  Calculator,
  FileCheck,
  Store,
  Shield
} from 'lucide-react';

// Admin navigation items
const adminNavItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/commissions', icon: DollarSign, label: 'Commissions' },
  { path: '/agents', icon: Users, label: 'Agents' },
  { path: '/statements', icon: FileText, label: 'Statements' },
  { path: '/expenses', icon: Receipt, label: 'Expenses' },
  { path: '/compliance', icon: Shield, label: 'Compliance' },
  { path: '/vendors', icon: Store, label: 'Vendors' },
  { path: '/referral-leads', icon: UserPlus, label: 'Referral Leads' },
  { path: '/reports', icon: BarChart3, label: 'Reports' },
  { path: '/notifications', icon: Bell, label: 'Notifications' },
  { path: '/audit-logs', icon: ScrollText, label: 'Audit Logs' },
  { path: '/email-templates', icon: Mail, label: 'Email Templates' },
  { path: '/import', icon: Upload, label: 'Import Data' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

// Agent navigation items (limited access)
const agentNavItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/commissions', icon: DollarSign, label: 'My Commissions' },
  { path: '/expenses', icon: Receipt, label: 'My Expenses' },
  { path: '/statements', icon: FileText, label: 'My Statements' },
  { path: '/my-tax-slips', icon: FileCheck, label: 'My Tax Slips' },
  { path: '/referral-leads', icon: UserPlus, label: 'My Referrals' },
  { path: '/notifications', icon: Bell, label: 'Notifications' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

export default function Sidebar({ open, setOpen }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { businessUnit, switchBusinessUnit } = useBusinessUnit();
  const [agentPhoto, setAgentPhoto] = useState('');

  // Select nav items based on user role
  const isAgent = user?.role === 'agent';
  const navItems = isAgent ? agentNavItems : adminNavItems;

  // Fetch agent's photo
  useEffect(() => {
    const fetchAgentData = async () => {
      if (isAgent && user?.agent_id) {
        try {
          const token = localStorage.getItem('token');
          const API = process.env.REACT_APP_BACKEND_URL;
          const response = await axios.get(`${API}/api/agents/${user.agent_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data) {
            setAgentPhoto(response.data.photo_url || '');
          }
        } catch (err) {
      // Error handled silently
    }
      }
    };
    fetchAgentData();
  }, [isAgent, user?.agent_id]);

  return (
    <aside
      data-testid="sidebar"
      className={`sidebar fixed left-0 top-0 h-screen z-40 transition-all duration-300 ${
        open ? 'w-64' : 'w-20'
      }`}
    >
      <div className="relative z-10 flex flex-col h-full">
        {/* Company Header */}
        <div className="px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            {open ? (
              <span className="text-white font-bold text-lg tracking-tight" style={{ fontFamily: 'Manrope' }}>
                Utmost Financial Inc.
              </span>
            ) : (
              <span className="text-white font-bold text-lg">UF</span>
            )}
          </div>
        </div>

        {/* Main Portal Link - Admin Only */}
        {!isAgent && open ? (
          <div className="px-3 py-2 border-b border-white/10">
            <Link
              to="/portal"
              data-testid="nav-portal-dashboard"
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                location.pathname === '/portal'
                  ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <PieChart className="w-4 h-4" />
              <span>Portal Overview</span>
            </Link>
            <Link
              to="/accounting"
              data-testid="nav-accounting"
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all mt-1 ${
                location.pathname === '/accounting'
                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Calculator className="w-4 h-4" />
              <span>Accounting</span>
            </Link>
            <Link
              to="/portal/referral-leads"
              data-testid="nav-portal-referrals"
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all mt-1 ${
                location.pathname === '/portal/referral-leads'
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <UserPlus className="w-4 h-4" />
              <span>All Referral Leads</span>
            </Link>
          </div>
        ) : !isAgent && (
          <div className="px-3 py-2 border-b border-white/10 space-y-1">
            <Link
              to="/portal"
              title="Portal Overview"
              className={`flex items-center justify-center p-2 rounded-lg transition-all ${
                location.pathname === '/portal'
                  ? 'bg-violet-500/20 text-violet-300'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <PieChart className="w-5 h-5" />
            </Link>
            <Link
              to="/accounting"
              title="Accounting"
              className={`flex items-center justify-center p-2 rounded-lg transition-all ${
                location.pathname === '/accounting'
                  ? 'bg-blue-500/20 text-blue-300'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Calculator className="w-5 h-5" />
            </Link>
            <Link
              to="/portal/referral-leads"
              title="All Referral Leads"
              className={`flex items-center justify-center p-2 rounded-lg transition-all ${
                location.pathname === '/portal/referral-leads'
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <UserPlus className="w-5 h-5" />
            </Link>
          </div>
        )}

        {/* Business Unit Tabs */}
        {open && (
          <div className="px-3 py-3 border-b border-white/10">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 px-1">Business Unit</p>
            <div className="flex flex-col gap-1">
              <button
                onClick={() => switchBusinessUnit('mortgage')}
                data-testid="bu-mortgage"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  businessUnit === 'mortgage'
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Home className="w-4 h-4" />
                <span>Mortgage</span>
              </button>
              <button
                onClick={() => switchBusinessUnit('insurance')}
                data-testid="bu-insurance"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  businessUnit === 'insurance'
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Briefcase className="w-4 h-4" />
                <span>Insurance & Investment</span>
              </button>
            </div>
          </div>
        )}

        {/* Collapsed Business Unit Indicator */}
        {!open && (
          <div className="px-3 py-3 border-b border-white/10 flex flex-col gap-1">
            <button
              onClick={() => switchBusinessUnit('mortgage')}
              title="Mortgage"
              className={`p-2 rounded-lg transition-all ${
                businessUnit === 'mortgage'
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Home className="w-5 h-5" />
            </button>
            <button
              onClick={() => switchBusinessUnit('insurance')}
              title="Insurance & Investment"
              className={`p-2 rounded-lg transition-all ${
                businessUnit === 'insurance'
                  ? 'bg-blue-500/20 text-blue-300'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Briefcase className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Role Badge */}
        {open && user && (
          <div className="px-4 py-2">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              isAgent 
                ? 'bg-emerald-500/20 text-emerald-300' 
                : 'bg-violet-500/20 text-violet-300'
            }`}>
              {isAgent ? <UserCircle className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
              {isAgent ? 'Agent' : 'Admin'}
            </span>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`nav-item flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${
                  isActive
                    ? 'active bg-white/15 text-white border-l-3 border-emerald-500'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                {open && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-white/10">
          {open && user && (
            <div className="mb-3 px-3 flex items-center gap-3">
              {/* Agent Photo */}
              {isAgent && agentPhoto ? (
                <img 
                  src={agentPhoto} 
                  alt={user.name} 
                  className="w-10 h-10 rounded-full object-cover border-2 border-emerald-500/50"
                  data-testid="agent-photo"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center">
                  <UserCircle className="w-6 h-6 text-slate-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user.name}</p>
                <p className="text-slate-400 text-xs truncate">{user.email}</p>
              </div>
            </div>
          )}
          {/* Collapsed state with photo */}
          {!open && isAgent && agentPhoto && (
            <div className="mb-2 flex justify-center">
              <img 
                src={agentPhoto} 
                alt={user?.name} 
                className="w-8 h-8 rounded-full object-cover border-2 border-emerald-500/50"
                data-testid="agent-photo-collapsed"
              />
            </div>
          )}
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors text-sm"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
            {open && <span>Logout</span>}
          </button>
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setOpen(!open)}
          data-testid="sidebar-toggle"
          className="absolute -right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 rounded-full bg-slate-700 border border-slate-600 flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
        >
          {open ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>
    </aside>
  );
}
